import Text from "common/Typography/Text";
import Button from "common/Buttons/Button";
import { useHistory } from "react-router-dom";
import Flex from "common/Boxes/Flex";
import CheckCircleFilled from "@ant-design/icons/CheckCircleFilled";
import ExtraQuotaText from "features/settings/Billing/ExtraQuotaText";
import CornerStarBanner from "common/misc/CornerStarBanner";
import { NextPlanData } from "@shared/features/settings/types";
import styled, { css, useTheme } from "styled-components";
import { Check, Sparkles } from "lucide-react";
import { NEW_LOOK_ICON_STROKE } from "common/NewLook";
import darkModeBackgroundElement from "images/quote_modal_background_dark_mode.png";
import backgroundElement from "images/quote_modal_background.png";
import {
  useHideBillingButtons,
  useUpgradePlanPath,
} from "features/settings/common/hooks.tsx";
import React, { ReactNode } from "react";
import SecureClientInviteHelperTooltip from "features/settings/Billing/SecureClientInviteHelperTooltip.tsx";
import { LanguageNameSpaces, UsageType } from "@shared/types";
import { getClientQuotaText } from "features/settings/common/utils.ts";
import { sharedLib } from "@shared/services/shared.service.ts";
import { useSelector } from "react-redux";
import { showIntercom } from "utils/misc.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";

interface PassedQuotaContentProps {
  currentUsageObjectText: string;
  nextPlanData?: NextPlanData;
  isPremiumPlanClientQuota?: boolean;
  additionalButton?: ReactNode;
  canPurchaseApiSlots?: boolean;
}

const PassedQuotaContent = ({
  currentUsageObjectText,
  nextPlanData,
  isPremiumPlanClientQuota,
  additionalButton,
  canPurchaseApiSlots,
}: PassedQuotaContentProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BILLING);
  const theme = useTheme();
  const history = useHistory();
  const removeNextPlanData = !nextPlanData;
  const upgradePath = useUpgradePlanPath();
  const user = useSelector(sharedLib.selectors.authentication.selectUser);
  const hideUpgradeButton = useHideBillingButtons();

  const getSortedNextPlanUsages = () => {
    return nextPlanData?.limits.map((limitData, index) => {
      const isLast = index === nextPlanData.limits.length - 1;
      const showSecureClientHelper =
        limitData.usageType === UsageType.SECURE_CLIENT_INVITE;
      return (
        <Flex
          $justifyContent="flex-start"
          style={{ marginBottom: 6 }}
          key={index}
        >
          {theme.isNewLook ? (
            <NewLookCheck>
              <Check size={14} strokeWidth={2} />
            </NewLookCheck>
          ) : (
            <CheckCircleFilled
              style={{
                color: theme.colors.primaryBlue,
                marginRight: 12,
                fontSize: 16,
              }}
            />
          )}
          <Flex
            $alignItems="flex-end"
            $justifyContent="space-between"
            style={{ flex: 1 }}
          >
            <Text style={{ fontWeight: 500 }} as="div">
              {limitData.baseLimit} {limitData.objectName}
              <ExtraQuotaText
                extraQuota={limitData.extraLimit}
                textStyle={{ fontWeight: 500 }}
              />
              {showSecureClientHelper && (
                <SecureClientInviteHelperTooltip
                  planType={user?.subscription_plan_type}
                />
              )}
            </Text>
            {isLast && <AndMoreText>+ {t("quota_modal.and_more")}</AndMoreText>}
          </Flex>
        </Flex>
      );
    });
  };

  const getHighlightedText = () => {
    return (
      <Background>
        {theme.isNewLook ? (
          <NewLookGetMore>
            <Sparkles size={16} strokeWidth={NEW_LOOK_ICON_STROKE} />
            {t("quota_modal.get_more", { plan: nextPlanData?.name })}
          </NewLookGetMore>
        ) : (
          <>
            <CornerStarBanner />
            <Text style={{ fontWeight: "700", marginBottom: 12 }}>
              {t("quota_modal.get_more", { plan: nextPlanData?.name })}
            </Text>
          </>
        )}
        {getSortedNextPlanUsages()}
      </Background>
    );
  };

  const getUpgradeButton = () => {
    if (!user?.isFree()) {
      return (
        <Button
          type="primary"
          onClick={() => {
            showIntercom();
          }}
          style={{ width: "100%" }}
        >
          {t("quota_modal.contact_support")}
        </Button>
      );
    } else if (!hideUpgradeButton) {
      return (
        <Button
          type="primary"
          onClick={() => history.push(upgradePath)}
          style={{ width: "100%" }}
        >
          {t("quota_modal.upgrade_now")}
        </Button>
      );
    }
  };

  return (
    <>
      <Text as="div" style={{ marginBottom: removeNextPlanData ? 0 : 20 }}>
        {t("quota_modal.plan_limited")}{" "}
        <span
          style={{
            fontWeight: theme.isNewLook
              ? theme.typography.fontWeights.MEDIUM
              : 700,
          }}
        >
          {currentUsageObjectText}.
        </span>
        {!removeNextPlanData && (
          <Text>{t("quota_modal.unlock_cornix_potential")}</Text>
        )}
      </Text>
      {!removeNextPlanData && (
        <>
          {getHighlightedText()}
          {isPremiumPlanClientQuota && (
            <Text style={{ margin: "20px 0 16px 0" }}>
              {getClientQuotaText(t, true, canPurchaseApiSlots)}
            </Text>
          )}
          {getUpgradeButton()}
          {additionalButton && (
            <AdditionalButtonWrapper>
              {additionalButton}
            </AdditionalButtonWrapper>
          )}
        </>
      )}
    </>
  );
};

export default PassedQuotaContent;

const Background = styled.div`
  background-image: url(${(p) =>
    p.theme.isDarkMode ? darkModeBackgroundElement : backgroundElement});
  background-size: cover;
  border-radius: 8px;
  margin-bottom: 20px;
  font-weight: 500;
  overflow: hidden;
  position: relative;
  padding: 20px;
  @media (max-width: 500px) {
    padding: 12px;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      /* What the next plan adds, in a quiet well. */
      padding: 16px;
      border-radius: ${p.theme.radii.control}px;
      border: none;
      background: ${getNewLookTokens(p.theme).well};
      box-shadow: inset 0 0 0 1px ${getNewLookTokens(p.theme).hairline};
      font-weight: ${p.theme.typography.fontWeights.NORMAL};
      .ant-typography {
        font-weight: ${p.theme.typography.fontWeights.MEDIUM} !important;
      }
    `}
`;
const NewLookGetMore = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  ${newLookType.panelTitle};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  svg {
    color: ${(p) => getNewLookTokens(p.theme).secondary};
  }
`;
/** A plain check in the label grey (no filled circle). */
const NewLookCheck = styled.span`
  flex: none;
  display: inline-flex;
  margin-right: 8px;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const AndMoreText = styled(Text)`
  font-weight: 500;
  margin-left: 20px;
  white-space: nowrap;
`;
const AdditionalButtonWrapper = styled.div`
  margin-top: 10px;
  display: flex;
  justify-content: center;
`;
